/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'rebass';

import { bool, func, objectOf, string } from 'prop-types';
import BasicSelect from '../../components/formInputs/BasicSelect';
import { AddContractGroups, ViewProductContractGroups } from '../productContracts';
import Spinner from '../../components/spinner/Spinner';

import { getAdminAccountsData, getSelectedAccountId } from './selectors';
import { fetchAdminAccounts, selectAdminAccount } from './actions';
import { accountType } from './propTypes';
import { getPendingStatus } from '../../util/requestReducer';

import { accountIdToEnglishName } from '../../util/chinaEnglishNameMappings';
import IconMenu from '../../components/IconMenu';
import AddAccountsModal from './modals/AddAccountModal';

import useModal from '../../hooks/useModal';

/**
 * @name sortAccounts
 * @desc sort accounts such that Greater China accounts appear first
 * @param {object} accounts
 */
const sortAccounts = accounts => {
  const accountValues = Object.values(accounts);
  const gcAccounts = [];
  const nonGcAccounts = [];

  accountValues.forEach(account => {
    if (account.region === 'CHINA') {
      gcAccounts.push(account);
    } else {
      nonGcAccounts.push(account);
    }
  });

  return [...gcAccounts, ...nonGcAccounts];
};

const AccountSelection = ({ accounts, isLoadingData, selectAdminAccount, selectedAccountId }) => {
  const history = useHistory();
  const [isAddAccountModalOpen, openAddAccountModal, closeAddAccountModal] = useModal();

  const accountsSorted = sortAccounts(accounts);

  const accountsTranslated = accountsSorted.map(account => {
    const accountEnglishName = accountIdToEnglishName[account.number];
    return accountEnglishName ? { ...account, name: `${account.name} - ${accountEnglishName}` } : account;
  });

  // Convert object of accounts to an array and map values into drop down list
  // Use account.number as value to enable search by account number.
  const accountList = useMemo(() => accountsTranslated, [JSON.stringify(accountsTranslated)]);

  const accountOptions = useMemo(
    () => accountList.map(({ number: value, name: label }) => ({ value, label: `${value} ${label}` })),
    [accountList]
  );

  // Find selected account and option by ID and Number respectively
  const selectedAccount = selectedAccountId && accountList.find(account => account.id === selectedAccountId);
  const selectedOption = selectedAccount && accountOptions.find(option => option.value === selectedAccount.number);

  return (
    <Flex flexWrap='wrap' pt='20px' pl='20px' width='100%'>
      <Flex flexFlow='row wrap' width='100%'>
        <Box sx={{ position: 'relative', width: '325px' }}>
          <BasicSelect
            name='adminAccountSelect'
            options={accountOptions}
            onChange={({ value }) => {
              const newAccount = accountList.find(account => account.number === value);

              selectAdminAccount(newAccount.id);
              history.push(`/accounts/departments/${newAccount.id}`);
            }}
            value={selectedOption}
            label='Select an Account'
            placeholder='Select Account'
            styles={{ width: '325px' }}
          />

          {isLoadingData && (
            <Box sx={{ position: 'absolute', right: '60px', top: '18px' }}>
              <Spinner />
            </Box>
          )}
        </Box>
        {selectedAccountId && (
          <ul
            css={css`
              align-items: center;
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              float: left;
              list-style-type: none;
              margin-left: 36px;
              & > li {
                &:nth-of-type(n + 2) {
                  margin-left: 18px;
                }
                & > span {
                  font-weight: bold;
                }
              }
            `}
          >
            <li>
              <span>Account Number:</span> {selectedAccount?.number}
            </li>
            <li>
              <span>Sales Org:</span> {selectedAccount?.salesOrg}
            </li>
            <li>
              <span>Direct Ship Allowed:</span> {selectedAccount?.directShipAllowed ? 'Yes' : 'No'}
            </li>
          </ul>
        )}
      </Flex>
      {selectedAccount && (
        <>
          <Flex alignItems='center' flexDirection='row' flexWrap='wrap' marginTop='20px'>
            <div
              className='gd-tableTextFilter ncss-input-container addContractGroups'
              style={{ width: '325px', overflow: 'visible' }}
            >
              <AddContractGroups />
            </div>
            <Box height='55px' marginLeft='36px' width='300px'>
              <ViewProductContractGroups />
            </Box>
          </Flex>
        </>
      )}
      <Flex justifyContent='flex-end' width='100%'>
        <IconMenu align='right' menuOptions={[{ text: 'Add Account', onClick: openAddAccountModal }]} />
      </Flex>
      {isAddAccountModalOpen && (
        <AddAccountsModal
          title='Add Accounts'
          closeModal={closeAddAccountModal}
          isOpen={isAddAccountModalOpen}
          actionName='Submit'
        />
      )}
    </Flex>
  );
};

AccountSelection.propTypes = {
  accounts: objectOf(accountType).isRequired,
  isLoadingData: bool.isRequired,
  selectedAccountId: string.isRequired,
  selectAdminAccount: func.isRequired
};

const mapStateToProps = state => ({
  accounts: getAdminAccountsData(state),
  isLoadingData: getPendingStatus(state, fetchAdminAccounts.event.name),
  selectedAccountId: getSelectedAccountId(state)
});

export default connect(mapStateToProps, { selectAdminAccount })(AccountSelection);
